// Aged care design layer.
//
// The section "looked like a table" because every tab hand-rolled its own markup: fifteen files,
// ninety-seven `rounded-2xl`s, a modal for everything, and a status pill styled differently in
// each. Plan Management had already built a shared vocabulary in ../PlanManagement/ui.jsx —
// DataTable, Toolbar, DetailDrawer, EmptyState, MoneyStat — and aged care never adopted it.
//
// So this file is deliberately THIN. It re-exports the product vocabulary and adds only what is
// specific to Support at Home: its status words, its service categories, deadlines as the primary
// information, and a page head set in the product's display face. One accent per role: teal is
// the module's wayfinding (the sidebar already says so); every action is the product's indigo
// btn-primary, the same as every other module. Nothing here introduces a third.

import React from 'react';
import clsx from 'clsx';
import { AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import {
  Currency, DataTable, DetailDrawer, EmptyState, ErrorState, Field, FilterChips, FormRow, KpiTile,
  MicroLabel, MoneyStat, OverflowMenu, SearchInput, SectionCard, StatRow, TableSkeleton,
  Toolbar, Tooltip, fmtDate, fmtDateTime, money,
} from '../PlanManagement/ui';

export {
  Currency, DataTable, DetailDrawer, EmptyState, ErrorState, Field, FilterChips, FormRow, KpiTile,
  MicroLabel, MoneyStat, OverflowMenu, SearchInput, SectionCard, StatRow, TableSkeleton,
  Toolbar, Tooltip, fmtDate, fmtDateTime, money,
};

// ── Section colour ───────────────────────────────────────────────────────────
// The page sits inside <main class="hue-teal">, so `hue-chip` / `hue-bar` / `hue-text` come out in
// the aged care teal (index.css). A tinted tile behind an icon is how a heading, a panel or a
// figure carries the section's colour; actions stay indigo and red / amber / green stay for state.
const ICON_SIZE = { sm: ['w-7 h-7 rounded-lg', 14], md: ['w-8 h-8 rounded-lg', 16], lg: ['w-10 h-10 rounded-xl', 19] };
export function HueIcon({ icon: Icon, size = 'md', className }) {
  if (!Icon) return null;
  const [box, px] = ICON_SIZE[size] || ICON_SIZE.md;
  return (
    <span className={clsx('hue-chip flex items-center justify-center shrink-0', box, className)} aria-hidden="true">
      <Icon size={px} />
    </span>
  );
}

// ── Page head ────────────────────────────────────────────────────────────────
// Bricolage Grotesque is the product's display face (tailwind `font-display`); using it here gives
// the heading presence without importing anything the rest of Rostery does not already load.
// Sentence case. No eyebrow label above it — the tab you clicked is the eyebrow.
// It is the screen's h1: the app header already names the module, so the screen name is the one
// top-level heading on the page (the same rule as the shared PageHeader).
// `icon` puts the screen's own glyph beside the heading in a teal tile, so every aged care screen
// is recognisable at a glance and carries the section's colour.
export function PageHead({ title, purpose, actions, icon, children }) {
  return (
    <div className="flex items-end justify-between gap-4 flex-wrap mb-5">
      <div className="min-w-0 flex items-start gap-3">
        <HueIcon icon={icon} size="lg" className="hidden sm:flex mt-0.5" />
        <div className="min-w-0">
          <h1 className="font-display text-[22px] leading-tight font-semibold tracking-tight text-slate-900 [text-wrap:balance]">{title}</h1>
          {purpose && <p className="text-[13px] leading-relaxed text-slate-500 mt-1 max-w-[65ch] [text-wrap:pretty]">{purpose}</p>}
          {children}
        </div>
      </div>
      {/* Wraps: three buttons side by side are wider than a phone, and must not push the page
          sideways. */}
      {actions && <div className="flex flex-wrap items-center gap-2 max-w-full">{actions}</div>}
    </div>
  );
}

// ── Panel ────────────────────────────────────────────────────────────────────
// ONE container radius across the whole section. Inner controls use rounded-lg; containers use
// rounded-xl; nothing uses rounded-2xl or a drop shadow. A border is the only edge.
export function Panel({ title, icon: Icon, actions, children, className, bodyClass, tone = 'white' }) {
  return (
    <div className={clsx('rounded-xl border border-slate-200 overflow-hidden', tone === 'muted' ? 'bg-slate-50/60' : 'bg-white', className)}>
      {(title || actions) && (
        <div className="px-4 py-2.5 border-b border-slate-100 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <HueIcon icon={Icon} size="sm" />
            {title && <h3 className="text-[13px] font-semibold text-slate-800 truncate">{title}</h3>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2 max-w-full">{actions}</div>}
        </div>
      )}
      <div className={bodyClass}>{children}</div>
    </div>
  );
}

// ── Status ───────────────────────────────────────────────────────────────────
// Every aged care status word, mapped once. Lookup is case-insensitive because production carries
// both 'Emailed' and 'sent' for the same fact, and a badge that fails to match falls to grey rather
// than to nothing.
const AC_STATUS = {
  // statements
  draft: 'badge-gray', emailed: 'badge-green', printed: 'badge-green', sent: 'badge-green', posted: 'badge-green', 'letters to post': 'badge-yellow',
  // claims
  submitted: 'badge-blue', reconciled: 'badge-green', paid: 'badge-green', rejected: 'badge-red',
  // incidents, complaints
  open: 'badge-yellow', investigating: 'badge-blue', 'in progress': 'badge-blue', in_progress: 'badge-blue',
  escalated: 'badge-red', resolved: 'badge-green', closed: 'badge-gray', notified: 'badge-green', overdue: 'badge-red',
  // leave, supplier invoices, purchase orders
  pending: 'badge-yellow', approved: 'badge-green', declined: 'badge-red', cancelled: 'badge-gray',
  matched: 'badge-green', unmatched: 'badge-yellow',
  // funding, care plans, participants
  active: 'badge-green', interim: 'badge-yellow', 'on hold': 'badge-yellow', on_hold: 'badge-yellow',
  ended: 'badge-gray', exited: 'badge-gray', archived: 'badge-gray', 'review due': 'badge-yellow',
  // worker compliance
  current: 'badge-green', expiring: 'badge-yellow', lapsed: 'badge-red', acknowledged: 'badge-green',
};
const titleCase = (s) => String(s).replace(/[_-]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

export function AcStatus({ status, label, className }) {
  const key = String(status == null ? '' : status).trim().toLowerCase();
  if (!key) return <span className="text-slate-300">—</span>;
  return <span className={clsx('badge', AC_STATUS[key] || 'badge-gray', className)}>{label || titleCase(key)}</span>;
}

// ── Service category ─────────────────────────────────────────────────────────
// The three Support at Home categories each keep one colour everywhere they appear (a chip in a
// table, a bar in a budget), so a category is recognised before it is read. Clinical, the one the
// participant never contributes to, is the section's own teal. None of the three is red, amber or
// green: those mean state.
export const CATEGORY_HUE = {
  clinical: { label: 'Clinical', bar: 'bg-teal-500', text: 'text-teal-800' },
  independence: { label: 'Independence', bar: 'bg-violet-500', text: 'text-violet-800' },
  everyday: { label: 'Everyday living', bar: 'bg-sky-500', text: 'text-sky-800' },
};
const CATEGORY = {
  clinical: { label: 'Clinical', cls: 'bg-teal-50 text-teal-800 ring-1 ring-teal-100' },
  independence: { label: 'Independence', cls: 'bg-violet-50 text-violet-800 ring-1 ring-violet-100' },
  everyday: { label: 'Everyday living', cls: 'bg-sky-50 text-sky-800 ring-1 ring-sky-100' },
};
export const normaliseCategory = (v) => {
  const t = String(v == null ? '' : v).toLowerCase().replace(/[^a-z]/g, '');
  if (t.startsWith('clinical')) return 'clinical';
  if (t.startsWith('independen')) return 'independence';
  if (t.startsWith('everyday')) return 'everyday';
  return null;
};
export function Category({ value, className }) {
  const k = normaliseCategory(value);
  const c = k ? CATEGORY[k] : null;
  if (!c) return <span className="text-slate-500 text-[12px]">{value || '—'}</span>;
  return <span className={clsx('inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-medium', c.cls, className)}>{c.label}</span>;
}

// ── Month ────────────────────────────────────────────────────────────────────
// A statement or claim month written the way a person says it ('August 2026'), never the ISO
// '2026-08'. Anything that is not a YYYY-MM month is shown as it came, or `empty` when blank.
export const monthLabel = (period, empty = '—') => {
  const m = /^(\d{4})-(\d{2})$/.exec(String(period || ''));
  if (!m) return period ? String(period) : empty;
  return new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
};

// ── Deadline ─────────────────────────────────────────────────────────────────
// In this domain the date something is due IS the information. Rendered relative, with the tone
// carrying urgency — the same three tones the dashboard's "what needs you" list uses, so a colour
// means the same thing on every screen.
export function Deadline({ date, done = false, className, absolute = true }) {
  if (!date) return <span className={clsx('text-slate-300', className)}>—</span>;
  const day = date instanceof Date ? date.toISOString().slice(0, 10) : String(date).slice(0, 10);
  const today = new Date().toISOString().slice(0, 10);
  const d = Math.round((new Date(day) - new Date(today)) / 86400000);
  if (done) return <span className={clsx('text-slate-500 tabular-nums', className)}>{fmtDate(day)}</span>;
  const rel = d === 0 ? 'due today' : d < 0 ? `${Math.abs(d)} day${Math.abs(d) === 1 ? '' : 's'} overdue` : `in ${d} day${d === 1 ? '' : 's'}`;
  const tone = d < 0 ? 'text-red-800 font-medium' : d <= 7 ? 'text-amber-700 font-medium' : 'text-slate-500';
  return (
    <span className={clsx('tabular-nums', tone, className)} title={fmtDate(day)}>
      {rel}{absolute && <span className="text-slate-500 font-normal"> · {fmtDate(day)}</span>}
    </span>
  );
}

// ── Severity row ─────────────────────────────────────────────────────────────
// A list where each row leads with a mark for how much it matters. Used instead of a card grid
// whenever items differ in urgency — cards flatten that difference; the row keeps it.
//
// The mark is a shape as well as a colour (an alert for overdue, a clock for due soon, a tick for
// done, a quiet dot for anything else), so urgency never rests on colour alone. It replaced a
// coloured left-edge stripe, which said the same thing in colour only.
//   severity: 'overdue' | 'soon' | 'done' | anything else ('open', 'ok', 'none') = dot
const MARK = {
  overdue: { Icon: AlertCircle, cls: 'text-red-700' },
  soon: { Icon: Clock, cls: 'text-amber-700' },
  done: { Icon: CheckCircle2, cls: 'text-emerald-600' },
};
function SeverityMark({ severity }) {
  const m = MARK[severity];
  return (
    <span className="w-5 h-5 shrink-0 flex items-center justify-center" aria-hidden="true">
      {m ? <m.Icon size={18} className={m.cls} /> : <span className="w-2 h-2 rounded-full bg-slate-300" />}
    </span>
  );
}
export function RailList({ children, className }) {
  return <div className={clsx('rounded-xl border border-slate-200 bg-white overflow-hidden divide-y divide-slate-100', className)}>{children}</div>;
}
export function RailRow({ severity = 'none', title, detail, right, onClick, className }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={clsx(
        'w-full flex items-start gap-3 px-4 py-3 text-left',
        onClick && 'hover:bg-slate-50 transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:bg-slate-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
        className,
      )}
    >
      <SeverityMark severity={severity} />
      <span className="flex-1 min-w-0">
        {/* Wraps rather than truncates: on a phone the right-hand side drops under the title
            instead of pushing the row wider than the screen. */}
        <span className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <span className="flex-1 min-w-[12rem] text-[14px] leading-5 text-slate-900 font-medium">{title}</span>
          {right && <span className="ml-auto max-w-full text-[13px] leading-5">{right}</span>}
        </span>
        {detail && <span className="block text-[13px] text-slate-500 mt-0.5 leading-snug">{detail}</span>}
      </span>
    </Tag>
  );
}

// ── Register ─────────────────────────────────────────────────────────────────
// The shape most tabs are: a titled list you can search and filter, with one action. Composes the
// shared Toolbar and DataTable so a register looks the same on every tab, and so the empty state
// always says what to do next rather than "No data".
export function Register({
  title, count, countLabel, icon, search, onSearch, searchPlaceholder, chips, actions,
  columns, rows, rowKey, loading, error, onRetry, onRowClick, activeRowKey, rowActions, rowTone,
  empty, dense = false, footer, className,
}) {
  return (
    <Panel className={className}>
      <Toolbar
        title={title} count={count} countLabel={countLabel} icon={icon} sticky={false}
        search={search} onSearch={onSearch} searchPlaceholder={searchPlaceholder}
        chips={chips}
        // Wrapped so several actions fold onto a second line on a phone instead of being cut off
        // at the panel's edge.
        actions={actions ? <div className="flex flex-wrap items-center justify-end gap-2 min-w-0">{actions}</div> : null}
        className="border-b-slate-100"
      />
      <DataTable
        columns={columns} rows={rows} rowKey={rowKey}
        loading={loading} error={error} onRetry={onRetry}
        onRowClick={onRowClick} activeRowKey={activeRowKey} rowActions={rowActions} rowTone={rowTone}
        empty={empty} dense={dense} stickyHeader={false} footer={footer}
        label={title}
      />
    </Panel>
  );
}

// ── Figure ───────────────────────────────────────────────────────────────────
// A number with its label under it, tabular so a row of them lines up. For the quiet strip of
// context at the top of a tab — never the headline.
// `icon` adds the figure's glyph in a teal tile beside the number.
export function Figure({ label, value, sub, tone = 'slate', icon, className }) {
  const TONE = { slate: 'text-slate-900', teal: 'text-teal-800', red: 'text-red-800', amber: 'text-amber-700', green: 'text-emerald-700', muted: 'text-slate-500' };
  return (
    <div className={clsx('min-w-0 flex items-start justify-between gap-3', className)}>
      <div className="min-w-0">
        <div className={clsx('font-display text-[22px] font-semibold tabular-nums leading-none tracking-[-0.01em]', TONE[tone] || TONE.slate)}>{value}</div>
        <div className="text-[12px] text-slate-500 mt-1.5">{label}</div>
        {sub && <div className="text-[11px] text-slate-500 mt-0.5 truncate">{sub}</div>}
      </div>
      <HueIcon icon={icon} size="sm" className="-mt-0.5" />
    </div>
  );
}
// A strip of figures joined by hairlines under one teal edge: the section's colour marks the top of
// a screen's numbers the way it marks a KPI tile elsewhere in Rostery.
// As many columns as figures (up to five) on a wide screen, so a strip never ends in an empty grey
// cell; on a phone, two a row, with an odd last figure taking the whole row.
const STRIP_COLS = { 1: 'lg:grid-cols-1', 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3', 4: 'lg:grid-cols-4', 5: 'lg:grid-cols-5' };
export function FigureStrip({ children, className }) {
  const items = React.Children.toArray(children).filter(Boolean);
  return (
    <div className={clsx('relative grid grid-cols-2 gap-px bg-slate-200 rounded-xl overflow-hidden border border-slate-200 mb-5', STRIP_COLS[Math.min(5, items.length)] || 'lg:grid-cols-4', className)}>
      <span className="hue-bar absolute inset-x-0 top-0 h-[3px] z-[1]" aria-hidden="true" />
      {items.map((c, i) => (
        <div key={i} className={clsx('bg-white px-4 pt-[15px] pb-3', i === items.length - 1 && items.length % 2 === 1 && 'col-span-2 lg:col-span-1')}>{c}</div>
      ))}
    </div>
  );
}
